import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CompetitionShareModal from "@/components/CompetitionShareModal";
import { getBattle, getBattleEvents, trackBattleAnalytics, shareCardUrl } from "@/lib/api";
import { trackCompetition } from "@/lib/competitionAnalytics";
import { entityLabel, entityPath, entityImage, fmtUSD, fmtNum } from "@/lib/format";

const ContributeModal = lazy(() => import("@/components/ContributeModal"));

function SideCard({ entity, backing, backers, contributions, rank, onBack, isLeader, completed }) {
  if (!entity) return null;
  const img = entityImage(entity);
  return (
    <div
      className={`rounded-2xl border px-4 py-5 ${isLeader ? "border-gold/40 bg-gold/[0.04]" : "border-white/10 bg-white/[0.02]"}`}
      data-testid={`battle-side-${entity.id}`}
    >
      <div className="flex items-center gap-3">
        {img ? (
          <img src={img} alt="" className="h-14 w-14 rounded-xl border border-white/10 object-cover" />
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-white/10 bg-zinc-900 font-display text-lg font-black">
            {entityLabel(entity).slice(0, 2).toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <Link to={entityPath(entity)} className="block truncate font-display text-xl font-black hover:text-gold">
            {entityLabel(entity)}
          </Link>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">
            {completed ? "Final" : `Battle rank #${rank}`} · {entity.chain || entity.category}
          </div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-600">Backing</div>
          <div className="font-mono font-bold text-gold">{fmtUSD(backing)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-600">Backers</div>
          <div className="font-mono font-bold">{fmtNum(backers)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-600">Backs</div>
          <div className="font-mono font-bold">{fmtNum(contributions)}</div>
        </div>
      </div>
      {!completed && (
        <button
          type="button"
          data-testid={`battle-back-${entity.id}`}
          onClick={() => {
            trackCompetition("BATTLE_BACK_CLICK", { entity_id: entity.id });
            trackBattleAnalytics(entity?._battleSlug, "BATTLE_BACK_CLICK").catch(() => {});
            onBack(entity);
          }}
          className="mt-4 w-full rounded-full bg-white py-3 font-display text-sm font-black uppercase tracking-wide text-black"
        >
          Back {entityLabel(entity)}
        </button>
      )}
    </div>
  );
}

function formatRemaining(seconds) {
  if (seconds == null) return null;
  const s = Math.max(0, Number(seconds) || 0);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h left`;
  if (h > 0) return `${h}h ${m}m left`;
  return `${m}m left`;
}

export default function BattlePage() {
  const { slug } = useParams();
  const [battle, setBattle] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backEntity, setBackEntity] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharePayload, setSharePayload] = useState(null);

  const load = useCallback(async () => {
    try {
      const [b, ev] = await Promise.all([
        getBattle(slug),
        getBattleEvents(slug, { limit: 30 }),
      ]);
      setBattle(b);
      setEvents(ev.events || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Battle not found");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    trackCompetition("BATTLE_VIEW", { slug });
    trackBattleAnalytics(slug, "BATTLE_VIEW").catch(() => {});
    load();
  }, [load, slug]);

  const completed = battle?.status === "completed" || !!battle?.finalized_at;
  const cancelled = battle?.status === "cancelled" || battle?.result_status === "cancelled";
  const tied = battle?.result_status === "tied" || (completed && battle?.tied);
  const winnerId = battle?.winner_entity_id;
  const leaderId = battle?.leader_entity_id;
  const aIsLeader = completed
    ? winnerId && battle?.entity_a?.id === winnerId
    : leaderId && battle?.entity_a?.id === leaderId;

  const centerCopy = (() => {
    if (!battle) return "";
    if (cancelled) return "This Battle was cancelled. Historical Backing is unchanged.";
    if (completed && tied) {
      return `DRAW — equal Verified Backing (${fmtUSD(battle.final_entity_a_backing_usd ?? battle.entity_a_battle_backing)})`;
    }
    if (completed && winnerId) {
      const winner = battle.entity_a?.id === winnerId ? battle.entity_a : battle.entity_b;
      return `WINNER — ${entityLabel(winner)} by ${fmtUSD(battle.final_margin_usd ?? battle.lead_over_opponent)}`;
    }
    const leader = aIsLeader ? battle.entity_a : battle.entity_b;
    const trailer = aIsLeader ? battle.entity_b : battle.entity_a;
    if (battle.tied) {
      return `${entityLabel(battle.entity_a)} and ${entityLabel(battle.entity_b)} are tied — ${fmtUSD(battle.gap_to_take_lead)} to take the lead`;
    }
    return `${entityLabel(leader)} leads by ${fmtUSD(battle.lead_over_opponent)} · ${entityLabel(trailer)} needs ${fmtUSD(battle.gap_to_take_lead)} to take the lead`;
  })();

  const openShare = (ev) => {
    const share = ev?.share || {};
    const headline =
      share.headline ||
      (completed && tied
        ? `${entityLabel(battle.entity_a)} and ${entityLabel(battle.entity_b)} tied their Battle`
        : completed && winnerId
          ? `${entityLabel(battle.entity_a?.id === winnerId ? battle.entity_a : battle.entity_b)} won its Battle`
          : battle?.title);
    setSharePayload({
      headline,
      shareText: share.headline || headline,
      canonicalPath: `/battle/${slug}`,
      shareImageUrl: ev?.id ? shareCardUrl(ev.id) : null,
      battleSlug: slug,
    });
    setShareOpen(true);
    trackBattleAnalytics(slug, "SHARE_INTENT").catch(() => {});
  };

  return (
    <div className="min-h-screen grain">
      <Navbar category="tokens" />
      <main className="mx-auto max-w-5xl px-4 py-8">
        <Link to="/battles" className="text-xs text-zinc-500 hover:text-white">← Battles</Link>
        {loading && <div className="mt-8 text-sm text-zinc-500">Loading battle…</div>}
        {error && <div className="mt-8 text-sm text-negative" data-testid="battle-error">{error}</div>}
        {battle && (
          <>
            <section className="mt-6" data-testid="battle-header">
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                {battle.metadata?.demo ? "BackOnFlip demo Battle" : "Battle"}
              </p>
              <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-5xl">
                {entityLabel(battle.entity_a)} vs {entityLabel(battle.entity_b)}
              </h1>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-zinc-500">
                <span className="rounded-full border border-white/10 px-3 py-1 uppercase tracking-wider" data-testid="battle-status">
                  {battle.status}
                </span>
                {completed && battle.result_status && (
                  <span
                    className="rounded-full border border-gold/30 px-3 py-1 uppercase tracking-wider text-gold"
                    data-testid="battle-result-status"
                  >
                    {battle.result_status === "tied" ? "DRAW" : battle.result_status === "won" ? "WINNER" : battle.result_status}
                  </span>
                )}
                {formatRemaining(battle.time_remaining_seconds) && (
                  <span className="rounded-full border border-white/10 px-3 py-1">{formatRemaining(battle.time_remaining_seconds)}</span>
                )}
                <button
                  type="button"
                  data-testid="battle-share-button"
                  onClick={() => openShare(events[0])}
                  className="rounded-full border border-white/15 px-3 py-1 uppercase tracking-wider text-white hover:border-gold/40 hover:text-gold"
                >
                  Share
                </button>
              </div>
              <p className="mt-4 font-display text-lg font-bold text-gold sm:text-2xl" data-testid="battle-lead-copy">
                {centerCopy}
              </p>
              {completed && (
                <p className="mt-2 text-sm text-zinc-400" data-testid="battle-final-stats">
                  Final Backing {fmtUSD(battle.final_entity_a_backing_usd ?? battle.entity_a_battle_backing)} vs{" "}
                  {fmtUSD(battle.final_entity_b_backing_usd ?? battle.entity_b_battle_backing)}
                  {battle.final_backers != null ? ` · ${fmtNum(battle.final_backers)} backers` : ""}
                  {battle.final_contributions != null ? ` · ${fmtNum(battle.final_contributions)} contributions` : ""}
                </p>
              )}
              <p className="mt-2 text-xs text-zinc-600">
                Score = Verified Backing inside the Battle window only.
              </p>
            </section>

            {(battle.sponsorship?.enabled || (battle.reward_pools && battle.reward_pools.length > 0)) && (
              <section
                className="mt-6 rounded-2xl border border-gold/25 bg-gold/[0.03] px-4 py-4"
                data-testid="sponsored-reward-panel"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-gold/40 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gold">
                    Sponsored Reward Pool
                  </span>
                  <span className="text-[11px] text-zinc-500">
                    {battle.sponsorship?.disclosure || "Sponsored reward pool"}
                  </span>
                </div>
                {(battle.reward_pools || []).map((pool) => {
                  const asset = pool.reward_asset || {};
                  const decimals = asset.decimals || 6;
                  const funded = (pool.funded_amount_base_units || 0) / (10 ** decimals);
                  const matching = pool.matching || {};
                  return (
                    <div key={pool.id} className="mt-3" data-testid={`reward-pool-${pool.slug}`}>
                      <div className="font-display text-lg font-black text-white">
                        Reward Pool: {funded.toLocaleString(undefined, { maximumFractionDigits: 2 })} {asset.symbol || "TOKEN"}
                      </div>
                      <p className="mt-1 text-xs text-zinc-500">
                        Community Verified Backing determines rank. This pool does not add to Battle score.
                      </p>
                      {matching.enabled && (
                        <div className="mt-2 text-sm text-zinc-300" data-testid="sponsor-match-progress">
                          Sponsor Match: {Math.round((matching.unlocked_display_ratio || 0) * 100)}% unlocked
                          <span className="text-zinc-600"> (incentive accounting only)</span>
                        </div>
                      )}
                      <p className="mt-2 text-[11px] text-zinc-600">{pool.disclaimer}</p>
                    </div>
                  );
                })}
              </section>
            )}

            <section className="mt-8 grid gap-4 md:grid-cols-2">
              <SideCard
                entity={{ ...battle.entity_a, _battleSlug: slug }}
                backing={battle.entity_a_battle_backing}
                backers={battle.entity_a_battle_backers}
                contributions={battle.entity_a_battle_contributions}
                rank={battle.entity_a_battle_rank}
                isLeader={!!aIsLeader || (tied && completed)}
                completed={completed || cancelled}
                onBack={setBackEntity}
              />
              <SideCard
                entity={{ ...battle.entity_b, _battleSlug: slug }}
                backing={battle.entity_b_battle_backing}
                backers={battle.entity_b_battle_backers}
                contributions={battle.entity_b_battle_contributions}
                rank={battle.entity_b_battle_rank}
                isLeader={!aIsLeader && !tied}
                completed={completed || cancelled}
                onBack={setBackEntity}
              />
            </section>

            <section className="mt-10" data-testid="battle-events">
              <h2 className="mb-3 text-[10px] uppercase tracking-[0.2em] text-zinc-600">Recent Battle events</h2>
              <div className="space-y-2">
                {events.map((ev) => (
                  <div key={ev.id} className="flex items-start justify-between gap-3 rounded-xl border border-white/10 px-4 py-3 text-sm text-zinc-300">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-600">{ev.event_type}</div>
                      <div className="mt-1">{ev.share?.headline || ev.event_type}</div>
                    </div>
                    <button
                      type="button"
                      className="shrink-0 text-xs uppercase tracking-wider text-zinc-500 hover:text-gold"
                      onClick={() => openShare(ev)}
                      data-testid={`battle-event-share-${ev.id}`}
                    >
                      Share
                    </button>
                  </div>
                ))}
                {!events.length && (
                  <div className="rounded-xl border border-white/10 px-4 py-6 text-sm text-zinc-500">
                    No competition events yet — Back to make it move.
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </main>
      {backEntity && (
        <Suspense fallback={null}>
          <ContributeModal
            entity={backEntity}
            open={!!backEntity}
            onClose={() => { setBackEntity(null); load(); }}
          />
        </Suspense>
      )}
      <CompetitionShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        headline={sharePayload?.headline}
        shareText={sharePayload?.shareText}
        canonicalPath={sharePayload?.canonicalPath}
        shareImageUrl={sharePayload?.shareImageUrl}
        battleSlug={sharePayload?.battleSlug}
      />
    </div>
  );
}
